import type { CompRef } from '@wix/document-services-types';
import { EditorElementLayout } from '@wix/component-protocol/schema';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';

import { isTPAPage } from './tpaUtils';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class LayoutPlatformService extends AbstractComponentsPlatformService {
  #isTopLevelElement(editorPointer: EditorPointer<CompRef>) {
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer);

    return !innerElementPath || innerElementPath.length === 0;
  }
  /**
   * note, we are not resolving layout for a virtual element...
   * we are getting editorPointer from the first added manifest,
   * because of it this api should work with single editorPointer only
   */
  getValue<K extends keyof EditorElementLayout>(
    editorPointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
    layoutProperty: K,
  ): EditorElementLayout[K] | null {
    const normalizedEditorPointers = [editorPointers].flat();

    /**
     * for inner elements 'disablePositioning' and 'disableRotation'
     * are always disabled
     */
    if (
      normalizedEditorPointers.length === 1 &&
      !this.#isTopLevelElement(normalizedEditorPointers[0]) &&
      (layoutProperty === 'disablePositioning' ||
        layoutProperty === 'disableRotation')
    ) {
      return true as EditorElementLayout[K];
    }

    const context = this.virtualElementProvider
      .buildVirtualElement({
        editorPointers: normalizedEditorPointers,
      })
      .getAPI();

    const layoutDefinitions = context.resolver.getLayoutDefinitions();
    return layoutDefinitions?.[layoutProperty] ?? null;
  }

  isDuplicatable(editorPointer: EditorPointer<CompRef>) {
    const componentType = this.dm.components.getType(
      editorPointer.documentPointer,
    );
    if (isTPAPage(this.dm, componentType)) {
      return false;
    }

    const shouldDisableDuplication = this.getValue(
      editorPointer,
      'disableDuplication',
    );

    if (shouldDisableDuplication == null) {
      return null;
    }
    return !shouldDisableDuplication;
  }
}
